export class AudioLevelDetector {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array<ArrayBuffer> | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private animationFrame: number | null = null;
  private onSpeakingChange: ((isSpeaking: boolean) => void) | null = null;
  private isSpeaking = false;
  private threshold = 30;
  private fadeOutTimer: number | null = null;
  private fadeOutDelay = 2000; // 2 seconds

  constructor(stream: MediaStream, onSpeakingChange: (isSpeaking: boolean) => void, threshold = 30) {
    this.threshold = threshold;
    this.onSpeakingChange = onSpeakingChange;
    this.init(stream);
  }

  private init(stream: MediaStream) {
    try {
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        return;
      }

      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;
      this.analyser.smoothingTimeConstant = 0.8;

      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength) as Uint8Array<ArrayBuffer>;

      this.source = this.audioContext.createMediaStreamSource(stream);
      this.source.connect(this.analyser);

      this.detectAudioLevel();
    } catch (error) {
      console.error('[AudioLevelDetector] Failed to initialize:', error);
    }
  }

  private detectAudioLevel = () => {
    if (!this.analyser || !this.dataArray) return;

    this.analyser.getByteFrequencyData(this.dataArray);

    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sum += this.dataArray[i];
    }
    const average = sum / this.dataArray.length;

    const currentlySpeaking = average > this.threshold;

    // If audio is active, set speaking immediately and clear any fade-out timer
    if (currentlySpeaking) {
      if (this.fadeOutTimer !== null) {
        clearTimeout(this.fadeOutTimer);
        this.fadeOutTimer = null;
      }
      if (!this.isSpeaking) {
        this.isSpeaking = true;
        if (this.onSpeakingChange) {
          this.onSpeakingChange(true);
        }
      }
    } else if (this.isSpeaking && this.fadeOutTimer === null) {
      // Audio stopped but we're still marked as speaking - start fade-out timer
      this.fadeOutTimer = window.setTimeout(() => {
        this.isSpeaking = false;
        this.fadeOutTimer = null;
        if (this.onSpeakingChange) {
          this.onSpeakingChange(false);
        }
      }, this.fadeOutDelay);
    }

    this.animationFrame = requestAnimationFrame(this.detectAudioLevel);
  };

  public destroy() {
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    if (this.fadeOutTimer !== null) {
      clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
    this.dataArray = null;
    this.onSpeakingChange = null;
  }
}
